import React, { useState } from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { NavigationProp, useRoute } from '@react-navigation/native';

import {
  enrollMasterKey,
  migrateLegacyMasterKey,
} from '../../services/encryption/MasterKeyService';
import { SecureStorageError } from '../../services/storage/SecureStorage';
import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';

import backIcon from '../../assets/icons/back.png';
import eyeIcon from '../../assets/icons/Eye.png';
import eyeClosedIcon from '../../assets/icons/Eye-closed.png';

const logger = createLogger('SetRecoveryCodeInput');

interface Props {
  navigation: NavigationProp<any>;
}

const MIN_LEN = 8;

/**
 * Step 2 of 3 — the user types a new recovery code + confirms.
 *
 * Attempts `migrateLegacyMasterKey` first (wraps the user's existing
 * legacy master key under the new KEK); if no legacy key exists (e.g.,
 * new signup who hasn't encrypted anything yet), falls back to
 * `enrollMasterKey` which generates a fresh random master key.
 *
 * Either path ends with the wrapped master key persisted on the
 * backend and cached in the keychain for the session. The success
 * screen takes it from there.
 */
const SetRecoveryCodeInputScreen: React.FC<Props> = ({ navigation }) => {
  const returnTo = (useRoute<any>().params as { returnTo?: unknown } | undefined)?.returnTo;
  const [code, setCode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit =
    code.length >= MIN_LEN && confirm.length >= MIN_LEN && !submitting;

  const handleSubmit = async () => {
    setErrorMessage(null);
    if (code.length < MIN_LEN) {
      setErrorMessage(`Recovery code must be at least ${MIN_LEN} characters`);
      return;
    }
    if (code !== confirm) {
      setErrorMessage('The two entries do not match. Try again.');
      return;
    }
    setSubmitting(true);
    try {
      // Prefer migrating an existing legacy master key so the user
      // doesn't lose access to documents they've already uploaded under
      // the auto-generated key.
      try {
        await migrateLegacyMasterKey(code);
      } catch (migrateErr) {
        const msg =
          migrateErr instanceof Error ? migrateErr.message : String(migrateErr);
        if (msg.toLowerCase().includes('no legacy master key')) {
          // New user with no encrypted data yet — enroll a fresh key.
          await enrollMasterKey(code);
        } else {
          throw migrateErr;
        }
      }
      navigation.replace('SetRecoveryCodeSuccess', { returnTo });
    } catch (err) {
      logger.error('Recovery-code enrollment failed');
      if (err instanceof SecureStorageError) {
        setErrorMessage(
          'Your recovery code was saved, but this device could not cache the vault key. Make sure Face ID, Touch ID, or a device passcode is enabled, then sign out and sign back in.',
        );
      } else {
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Set Recovery Code</Text>
        <View style={{ width: ms(24) }} />
      </View>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.stepLabel}>Step 2 of 3</Text>
          <Text style={styles.title}>Enter your recovery code</Text>
          <Text style={styles.description}>
            At least {MIN_LEN} characters. Longer is better. Mix of letters,
            numbers, and symbols is ideal but not required.
          </Text>

          <View style={styles.inputRow}>
            <TextInput
              style={styles.input}
              placeholder="Recovery code"
              placeholderTextColor="#9A9A9A"
              value={code}
              onChangeText={setCode}
              secureTextEntry={!showCode}
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
              textContentType="newPassword"
              accessibilityLabel="Recovery code input"
            />
            <TouchableOpacity
              style={styles.eyeToggle}
              onPress={() => setShowCode((v) => !v)}
              accessibilityRole="button"
              accessibilityLabel={showCode ? 'Hide code' : 'Show code'}
            >
              <Image
                source={showCode ? eyeIcon : eyeClosedIcon}
                style={styles.eyeIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputRow}>
            <TextInput
              style={styles.input}
              placeholder="Confirm recovery code"
              placeholderTextColor="#9A9A9A"
              value={confirm}
              onChangeText={setConfirm}
              secureTextEntry={!showCode}
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="newPassword"
              accessibilityLabel="Confirm recovery code"
            />
          </View>

          {errorMessage ? (
            <Text style={styles.error} accessibilityRole="alert">
              {errorMessage}
            </Text>
          ) : null}

          <TouchableOpacity
            style={[
              styles.primaryButton,
              !canSubmit && styles.primaryButtonDisabled,
            ]}
            onPress={handleSubmit}
            disabled={!canSubmit}
            accessibilityRole="button"
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.primaryButtonText}>Save recovery code</Text>
            )}
          </TouchableOpacity>

          <Text style={styles.footnote}>
            After saving, you'll see a confirmation with a reminder to write
            the code down somewhere safe.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  backButton: {
    padding: s(4),
  },
  backIcon: {
    width: ms(24),
    height: ms(24),
    tintColor: '#333',
  },
  headerTitle: {
    fontSize: ms(16),
    fontWeight: '600',
    color: '#1A1A1A',
  },
  content: {
    padding: s(20),
  },
  stepLabel: {
    fontSize: ms(12),
    fontWeight: '600',
    color: '#29BFC0',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: vs(4),
  },
  title: {
    fontSize: ms(22),
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: vs(8),
  },
  description: {
    fontSize: ms(14),
    color: '#5A5A5A',
    lineHeight: ms(22),
    marginBottom: vs(20),
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D8DBDD',
    borderRadius: ms(8),
    marginBottom: vs(12),
    paddingHorizontal: s(12),
  },
  input: {
    flex: 1,
    fontSize: ms(15),
    color: '#1A1A1A',
    paddingVertical: vs(14),
  },
  eyeToggle: {
    padding: s(8),
  },
  eyeIcon: {
    width: ms(20),
    height: ms(20),
    tintColor: '#666',
  },
  error: {
    fontSize: ms(13),
    color: '#E85C5C',
    marginBottom: vs(12),
    marginTop: vs(4),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: vs(14),
    paddingHorizontal: s(40),
    borderRadius: ms(8),
    alignItems: 'center',
    marginTop: vs(8),
  },
  primaryButtonDisabled: {
    backgroundColor: '#B5E3E3',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16),
    fontWeight: '600',
  },
  footnote: {
    fontSize: ms(12),
    color: '#9A9A9A',
    textAlign: 'center',
    marginTop: vs(20),
    lineHeight: ms(18),
  },
});

export default SetRecoveryCodeInputScreen;
